import React, { useEffect, useRef, ReactElement } from 'react';
import { observer } from 'mobx-react-lite';
import { useMst } from '../../models/stores/Root';
import Header from '../../components/framework/Header';
import Footer from '../../components/framework/Footer';
import CheckoutPageContainer from '../../components/framework/CheckoutPageContainer';
import CartSummarySection from '../../components/framework/CartSummarySection';
import Divider from '../../components/content/Divider';
import PayPalView from '../views/PayPalView';
import EditPaymentView from '../views/EditPaymentView';
import { PayPalOnApproveData } from '../../types/PayPal';

const PayPalPage: React.FC = observer(() => {
  const rootStore = useMst();
  const { configuration, customer, cart, currentStep } = rootStore;
  const checkoutDivRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const initialize = async (): Promise<void> => {
      const { payerID, orderID, paymentID } = rootStore.machineState?.context
        .paypalData as PayPalOnApproveData;
      await rootStore.initializePayPalCheckout(
        payerID,
        configuration.features.enablePayPalPpcp ? orderID : paymentID
      );
      await rootStore.postStripeIntentFlow();
    };

    if (rootStore.stateMatches('PayPal.Checkout.Loading')) {
      initialize();
    }
  }, [
    configuration.features.enablePayPalPpcp,
    customer.isLoading,
    cart.shippingAddress,
    customer.preferredShippingAddress,
    rootStore,
    currentStep,
  ]);

  const scrollTopOfPage = (): void => {
    if (checkoutDivRef.current?.scrollTo) {
      checkoutDivRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleLogoutClick = async (): Promise<void> => {
    await customer.logoutFlow();
  };

  const handleEditPaymentClicked = (): void => {
    rootStore.send({ type: 'EDIT_PAYMENT' });
  };

  const handleEditPaymentComplete = (): void => {
    if (cart.cartData.isPayPal) {
      if (cart.cartData.totals.grandTotal === 0) {
        cart.resetPayPalPaymentProfile();
        rootStore.send({ type: 'PAYPAL_EXIT', paypalParams: null });
      } else {
        rootStore.send({ type: 'EDIT_PAYMENT_COMPLETE' });
      }
    } else {
      rootStore.send({ type: 'PAYPAL_EXIT', paypalParams: null });
    }
  };

  const renderView = (): ReactElement | null => {
    if (customer.isLoading) {
      return null;
    }
    // if stateMatches or currentStep corresponds to an edit view, return it instead
    if (rootStore.stateMatches('PayPal.Checkout.Payment')) {
      return <EditPaymentView onEditComplete={handleEditPaymentComplete} />;
    }
    return (
      <PayPalView
        onLogout={handleLogoutClick}
        onEditPayment={handleEditPaymentClicked}
        onScrollTopOfPage={scrollTopOfPage}
      />
    );
  };

  return (
    <CheckoutPageContainer cart={<CartSummarySection />} ref={checkoutDivRef}>
      <Header
        logoText={configuration.logo.text ?? ''}
        logoPublicId={configuration.logo.publicId ?? undefined}
      />
      <Divider className="md:hidden" />
      {renderView()}
      <Footer showTermsAndConditions />
    </CheckoutPageContainer>
  );
});

export default PayPalPage;
